import * as React from 'react';
import { useState } from 'react';
import LGAS from "../constants/nigeria-lgas.json";
import {
    Box, Container, Typography, Button, TextField, MenuItem,
    LinearProgress, Chip, GlobalStyles, Snackbar, Alert,
} from '@mui/material';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import { CheckCircleOutline, ArrowForward, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Logo: React.FC<{ size?: number }> = ({ size = 36 }) => (
    <Box
        component="img"
        src="images/ngc.png"
        alt="NG Credit"
        sx={{ width: size, height: size, borderRadius: `${size * 0.18}px`, display: 'block', flexShrink: 0 }}
    />
);

const BRAND = {
    purple:      '#7B5EA7',
    purpleLight: '#9B7EC8',
    purpleDark:  '#3D2A6B',
    purpleDeep:  '#1A0F3D',
    lavenderSoft:'#F7F4FC',
    accent:      '#C8B8E8',
    ink:         '#0D0A1A',
    cream:       '#FAF9FC',
} as const;

const theme = createTheme({
    palette: {
        primary:   { main: BRAND.purple, light: BRAND.purpleLight, dark: BRAND.purpleDark },
        background:{ default: BRAND.cream, paper: '#fff' },
        text:      { primary: BRAND.ink, secondary: '#5A5070' },
    },
    typography: {
        fontFamily: "'DM Sans', system-ui, sans-serif",
        h1: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
        h2: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
        h3: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
        h4: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 100, textTransform: 'none', fontWeight: 700 },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.purple },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.purple, borderWidth: 2 },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: BRAND.purple },
                },
            },
        },
    },
});

const AppGlobalStyles: React.FC = () => (
    <GlobalStyles styles={`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body { overflow-x: hidden; background: ${BRAND.cream}; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:none; } }
        @keyframes pulse { 0%,100% { opacity:.6; } 50% { opacity:1; } }
    `} />
);

interface FormData {
    fullName: string; email: string; phone: string; state: string; lga: string;
    businessName: string; partnerType: string; businessAge: string; employees: string;
    annualRevenue: string; website: string;
    creditPurpose: string; amountNeeded: string; timeline: string; assetType: string;
    educationLevel: string; fieldOfStudy: string; certifications: string;
    yearsExperience: string; previousFinancing: string; keyAchievements: string;
    whyNGCredit: string; referralSource: string; agreeTerms: boolean;
}

const INITIAL: FormData = {
    fullName: '', email: '', phone: '', state: '', lga: '',
    businessName: '', partnerType: '', businessAge: '', employees: '',
    annualRevenue: '', website: '',
    creditPurpose: '', amountNeeded: '', timeline: '', assetType: '',
    educationLevel: '', fieldOfStudy: '', certifications: '',
    yearsExperience: '', previousFinancing: '', keyAchievements: '',
    whyNGCredit: '', referralSource: '', agreeTerms: false,
};

// ─── Validation rules per step ────────────────────────────────────────────────
// Returns an array of field keys that are required but empty for the given step
function getMissingFields(step: number, form: FormData): (keyof FormData)[] {
    switch (step) {
        case 1:
            return (['fullName', 'email', 'phone', 'state', 'lga'] as (keyof FormData)[])
                .filter(k => !String(form[k]).trim());
        case 2:
            return (['businessName', 'partnerType'] as (keyof FormData)[])
                .filter(k => !String(form[k]).trim());
        case 3:
            return (['creditPurpose', 'assetType', 'amountNeeded'] as (keyof FormData)[])
                .filter(k => !String(form[k]).trim());
        case 4:
            return (['keyAchievements'] as (keyof FormData)[])
                .filter(k => !String(form[k]).trim());
        case 5:
            return (['whyNGCredit'] as (keyof FormData)[])
                .filter(k => !String(form[k]).trim());
        default:
            return [];
    }
}

const PARTNER_TYPES = ['Greenhouse Farmer', 'Fish Pond Operator', 'Aquaculture Business', 'Plastic Waste Collector', 'Eko Bricks Processor', 'Agricultural SME', 'Food Processor', 'Other'];
const ASSET_TYPES   = ['Greenhouse Structure', 'Drip Irrigation System', 'Fish Pond Setup', 'Cold Chain / Processing Equipment', 'Waste Collection Infrastructure', 'Plastic Grinding Equipment', 'Other'];
const STATES        = ['Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno','Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba','Yobe','Zamfara'];
const EDUCATION     = ['No Formal Education', 'Primary School', 'Secondary School (WAEC/NECO)', 'OND / NCE', 'HND', "Bachelor's Degree", "Master's Degree", 'PhD', 'Vocational / Technical Training'];
const AMOUNTS       = ['Under ₦5M', '₦5M – ₦15M', '₦15M – ₦50M', '₦50M – ₦200M', 'Over ₦200M'];
const TIMELINES     = ['Immediately', '1–3 months', '3–6 months', '6–12 months'];
const REVENUES      = ['Pre-revenue', 'Under ₦5M/year', '₦5M–₦20M/year', '₦20M–₦100M/year', 'Over ₦100M/year'];
const EMPLOYEES_OPT = ['Solo (just me)', '2–5', '6–20', '21–50', '50+'];

const STEPS = [
    { id: 1, label: 'Contact',   short: 'Contact' },
    { id: 2, label: 'Business',  short: 'Business' },
    { id: 3, label: 'Purpose',   short: 'Purpose' },
    { id: 4, label: 'Expertise', short: 'Expertise' },
    { id: 5, label: 'Finish',    short: 'Finish' },
];

const Field: React.FC<{
    label: string; name: keyof FormData; value: string;
    onChange: (k: keyof FormData, v: string) => void;
    multiline?: boolean; rows?: number; select?: boolean;
    options?: string[]; required?: boolean; type?: string; placeholder?: string;
    error?: boolean;
}> = ({ label, name, value, onChange, multiline, rows, select, options, required, type, placeholder, error }) => (
    <TextField
        fullWidth label={label} value={value} type={type || 'text'}
        placeholder={placeholder}
        required={required}
        multiline={multiline} rows={rows || 1}
        select={select}
        error={error}
        helperText={error ? 'This field is required' : undefined}
        onChange={e => onChange(name, e.target.value)}
        variant="outlined" size="small"
    >
        {select && options?.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: '.65rem', letterSpacing: '.18em', textTransform: 'uppercase',
        color: BRAND.purple, fontWeight: 700, mb: 2, mt: 1 }}>
        {children}
    </Typography>
);

const Grid: React.FC<{ children: React.ReactNode; cols?: number }> = ({ children, cols = 2 }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${cols}, 1fr)` }, gap: 2, mb: 1 }}>
        {children}
    </Box>
);

const Apply: React.FC = () => {
    const navigate    = useNavigate();
    const [step, setStep]           = useState(1);
    const [form, setForm]           = useState<FormData>(INITIAL);
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending]     = useState(false);
    const [snack, setSnack]           = useState(false);
    // Track which fields have been flagged as errors (only shown after a failed attempt to proceed)
    const [errorFields, setErrorFields] = useState<(keyof FormData)[]>([]);

    const set = (k: keyof FormData, v: string | boolean) => {
        setForm(prev => ({ ...prev, [k]: v }));
        // Clear error on that field as soon as user fills it
        setErrorFields(prev => prev.filter(f => f !== k));
    };

    const handleNext = () => {
        const missing = getMissingFields(step, form);
        if (missing.length > 0) {
            setErrorFields(missing);
            // Scroll to top of card so user sees the errors
            window.scrollTo({ top: 200, behavior: 'smooth' });
            return;
        }
        setErrorFields([]);
        setStep(s => s + 1);
    };

    const handleSubmit = async () => {
        // Final gate: must agree to terms + step 5 required fields
        const missing = getMissingFields(5, form);
        if (missing.length > 0 || !form.agreeTerms) {
            setErrorFields(missing);
            return;
        }
        setSending(true);
        const subject = encodeURIComponent(`Partner Application — ${form.businessName || form.fullName}`);
        const body = encodeURIComponent(`
PARTNER APPLICATION — NG CREDIT
================================

CONTACT DETAILS
Full Name:          ${form.fullName}
Email:              ${form.email}
Phone:              ${form.phone}
State:              ${form.state}
LGA:                ${form.lga}

BUSINESS INFORMATION
Business Name:      ${form.businessName}
Partner Type:       ${form.partnerType}
Business Age:       ${form.businessAge}
Employees:          ${form.employees}
Annual Revenue:     ${form.annualRevenue}
Website:            ${form.website || 'N/A'}

CREDIT PURPOSE
Purpose:            ${form.creditPurpose}
Asset Needed:       ${form.assetType}
Amount Needed:      ${form.amountNeeded}
Timeline:           ${form.timeline}

EDUCATION & EXPERTISE
Education Level:    ${form.educationLevel}
Field of Study:     ${form.fieldOfStudy}
Certifications:     ${form.certifications || 'N/A'}
Years Experience:   ${form.yearsExperience}
Previous Financing: ${form.previousFinancing || 'N/A'}
Key Achievements:   ${form.keyAchievements}

ADDITIONAL
Why NG Credit:      ${form.whyNGCredit}
Referral Source:    ${form.referralSource}

---
Submitted via ng-credit.com
        `.trim());
        window.location.href = `mailto:corporate@ng-credit.com?subject=${subject}&body=${body}`;
        setTimeout(() => {
            setSending(false);
            setSubmitted(true);
        }, 800);
    };

    const progress = ((step - 1) / (STEPS.length - 1)) * 100;
    const isError = (field: keyof FormData) => errorFields.includes(field);

    if (submitted) {
        return (
            <ThemeProvider theme={theme}>
                <AppGlobalStyles />
                <Box sx={{
                    minHeight: '100vh', background: BRAND.purpleDeep,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3,
                }}>
                    <Box sx={{ textAlign: 'center', animation: 'fadeUp .7s ease both' }}>
                        <CheckCircleOutline sx={{ fontSize: 64, color: '#4caf50', mb: 3 }} />
                        <Typography variant="h2" sx={{ color: '#fff', fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}>
                            Application Submitted
                        </Typography>
                        <Typography sx={{ color: alpha('#fff', .6), fontSize: '1rem', maxWidth: 420, mx: 'auto', lineHeight: 1.8, mb: 4 }}>
                            Thank you, <strong style={{ color: BRAND.accent }}>{form.fullName}</strong>. Your application has been sent to our team at corporate@ng-credit.com. We'll be in touch within 3–5 business days.
                        </Typography>
                        <Button variant="contained" onClick={() => navigate('/')}
                                sx={{ background: BRAND.purple, px: 5, py: 1.6, fontSize: '.95rem',
                                    '&:hover': { background: BRAND.purpleLight } }}>
                            Back to Home
                        </Button>
                    </Box>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <AppGlobalStyles />

            <Box sx={{
                background: `linear-gradient(135deg, ${BRAND.purpleDeep} 0%, ${BRAND.purpleDark} 100%)`,
                px: { xs: 3, md: 6 }, py: 2.5,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                     onClick={() => navigate('/')}>
                    <Logo size={32} />
                    <Typography sx={{ fontFamily: "'Instrument Serif',serif", fontSize: '1.2rem', color: '#fff' }}>
                        NG Credit
                    </Typography>
                </Box>
                <Typography sx={{ color: alpha('#fff', .4), fontSize: '.78rem' }}>
                    Partner Application
                </Typography>
            </Box>

            <LinearProgress variant="determinate" value={progress}
                            sx={{ height: 3, backgroundColor: alpha(BRAND.purple, .15),
                                '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${BRAND.purple}, ${BRAND.purpleLight})` } }} />

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 }, px: { xs: 2, md: 4 } }}>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'flex-start',
                        gap: 0,
                        width: '100%', maxWidth: 340,
                        justifyContent: 'space-between',
                    }}>
                        {STEPS.map((s, idx) => (
                            <Box key={s.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .75, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative', justifyContent: 'center' }}>
                                    {idx > 0 && (
                                        <Box sx={{
                                            position: 'absolute', left: 0, right: '50%',
                                            height: 2, top: 15,
                                            background: step > s.id ? BRAND.purple : alpha(BRAND.purple, .15),
                                            transition: 'background .3s',
                                        }} />
                                    )}
                                    {idx < STEPS.length - 1 && (
                                        <Box sx={{
                                            position: 'absolute', left: '50%', right: 0,
                                            height: 2, top: 15,
                                            background: step > s.id ? BRAND.purple : alpha(BRAND.purple, .15),
                                            transition: 'background .3s',
                                        }} />
                                    )}
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: '50%', position: 'relative', zIndex: 1,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: step > s.id ? BRAND.purple : step === s.id ? BRAND.purpleDeep : '#fff',
                                        border: `2px solid ${step >= s.id ? BRAND.purple : alpha(BRAND.purple, .2)}`,
                                        transition: 'all .3s',
                                    }}>
                                        {step > s.id
                                            ? <CheckCircleOutline sx={{ fontSize: 16, color: '#fff' }} />
                                            : <Typography sx={{ fontSize: '.7rem', fontWeight: 700,
                                                color: step === s.id ? '#fff' : alpha(BRAND.purple, .4) }}>{s.id}</Typography>
                                        }
                                    </Box>
                                </Box>
                                <Typography sx={{
                                    fontSize: '.58rem', fontWeight: 600, letterSpacing: '.05em',
                                    color: step >= s.id ? BRAND.purple : alpha(BRAND.ink, .3),
                                    display: { xs: 'none', sm: 'block' },
                                    textAlign: 'center',
                                }}>
                                    {s.short.toUpperCase()}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Error banner */}
                {errorFields.length > 0 && (
                    <Box sx={{
                        mb: 2, px: 2.5, py: 1.5,
                        background: alpha('#d32f2f', .07),
                        border: `1px solid ${alpha('#d32f2f', .2)}`,
                        borderRadius: 2,
                        display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                        <Typography sx={{ fontSize: '.82rem', color: '#c62828' }}>
                            Please fill in all required fields before continuing.
                        </Typography>
                    </Box>
                )}

                <Box sx={{
                    background: '#fff', borderRadius: 4,
                    boxShadow: `0 8px 48px ${alpha(BRAND.purpleDark, .08)}`,
                    p: { xs: 3, md: 5 },
                    animation: 'fadeUp .5s ease both',
                }}>
                    {/* ── STEP 1 ── */}
                    {step === 1 && (
                        <>
                            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.purpleDark, mb: .75 }}>
                                Contact Information
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '.9rem', mb: 3.5, lineHeight: 1.7 }}>
                                Tell us who you are. This is where we'll send your application response.
                            </Typography>
                            <SectionLabel>Personal Details</SectionLabel>
                            <Grid>
                                <Field label="Full Name" name="fullName" value={form.fullName} onChange={set} required error={isError('fullName')} />
                                <Field label="Email Address" name="email" value={form.email} onChange={set} type="email" required error={isError('email')} />
                            </Grid>
                            <Grid>
                                <Field label="Phone Number" name="phone" value={form.phone} onChange={set} type="tel" placeholder="e.g. 08012345678" required error={isError('phone')} />
                                <Field label="State" name="state" value={form.state} onChange={set} select options={STATES} required error={isError('state')} />
                            </Grid>
                            <Grid cols={1}>
                                <Field label="Local Government Area (LGA)" name="lga" value={form.lga} onChange={set} select options={form.state ? (LGAS as Record<string, string[]>)[form.state] || [] : []} required error={isError('lga')} />
                            </Grid>
                        </>
                    )}

                    {/* ── STEP 2 ── */}
                    {step === 2 && (
                        <>
                            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.purpleDark, mb: .75 }}>
                                Business Information
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '.9rem', mb: 3.5, lineHeight: 1.7 }}>
                                Help us understand your operation. We finance SMEs across agriculture, aquaculture, and circular materials.
                            </Typography>
                            <SectionLabel>Your Business</SectionLabel>
                            <Grid>
                                <Field label="Business / Farm Name" name="businessName" value={form.businessName} onChange={set} required error={isError('businessName')} />
                                <Field label="Partner Type" name="partnerType" value={form.partnerType} onChange={set} select options={PARTNER_TYPES} required error={isError('partnerType')} />
                            </Grid>
                            <Grid>
                                <Field label="How long has the business been operating?" name="businessAge" value={form.businessAge}
                                       onChange={set} select options={['Less than 1 year', '1–2 years', '3–5 years', '5–10 years', 'Over 10 years']} />
                                <Field label="Number of Employees" name="employees" value={form.employees} onChange={set} select options={EMPLOYEES_OPT} />
                            </Grid>
                            <Grid>
                                <Field label="Annual Revenue (approx.)" name="annualRevenue" value={form.annualRevenue} onChange={set} select options={REVENUES} />
                                <Field label="Website / Social Media (optional)" name="website" value={form.website} onChange={set} placeholder="https://" />
                            </Grid>
                        </>
                    )}

                    {/* ── STEP 3 ── */}
                    {step === 3 && (
                        <>
                            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.purpleDark, mb: .75 }}>
                                Credit Purpose
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '.9rem', mb: 3.5, lineHeight: 1.7 }}>
                                What would you deploy NG Credit financing for? Be specific — this helps us match the right asset structure.
                            </Typography>
                            <SectionLabel>What You Need</SectionLabel>
                            <Grid cols={1}>
                                <Field label="Describe your credit purpose" name="creditPurpose" value={form.creditPurpose}
                                       onChange={set} multiline rows={3}
                                       placeholder="e.g. I want to set up a 500sqm drip-irrigated greenhouse for tomato farming in Ogun State..." required error={isError('creditPurpose')} />
                            </Grid>
                            <Grid>
                                <Field label="Asset Type Required" name="assetType" value={form.assetType} onChange={set} select options={ASSET_TYPES} required error={isError('assetType')} />
                                <Field label="Estimated Amount Needed" name="amountNeeded" value={form.amountNeeded} onChange={set} select options={AMOUNTS} required error={isError('amountNeeded')} />
                            </Grid>
                            <Grid cols={1}>
                                <Field label="Preferred Timeline" name="timeline" value={form.timeline} onChange={set} select options={TIMELINES} />
                            </Grid>
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', mb: 1 }}>We currently finance:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {ASSET_TYPES.map(a => (
                                        <Chip key={a} label={a} size="small"
                                              onClick={() => set('assetType', a)}
                                              sx={{ fontSize: '.72rem', borderRadius: 2,
                                                  background: form.assetType === a ? BRAND.purple : alpha(BRAND.purple, .07),
                                                  color: form.assetType === a ? '#fff' : BRAND.purpleDark,
                                                  cursor: 'pointer', border: 'none',
                                                  '&:hover': { background: form.assetType === a ? BRAND.purpleLight : alpha(BRAND.purple, .14) }
                                              }} />
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* ── STEP 4 ── */}
                    {step === 4 && (
                        <>
                            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.purpleDark, mb: .75 }}>
                                Education & Expertise
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '.9rem', mb: 3.5, lineHeight: 1.7 }}>
                                We back operators, not just plans. Tell us about your background and track record.
                            </Typography>
                            <SectionLabel>Educational Background</SectionLabel>
                            <Grid>
                                <Field label="Highest Education Level" name="educationLevel" value={form.educationLevel} onChange={set} select options={EDUCATION} />
                                <Field label="Field of Study / Trade" name="fieldOfStudy" value={form.fieldOfStudy} onChange={set} placeholder="e.g. Agriculture, Civil Engineering" />
                            </Grid>
                            <Grid cols={1}>
                                <Field label="Relevant Certifications or Training (optional)" name="certifications" value={form.certifications}
                                       onChange={set} placeholder="e.g. NASC certified, FADAMA training, FAO workshop..." />
                            </Grid>
                            <SectionLabel>Professional Experience</SectionLabel>
                            <Grid>
                                <Field label="Years of Experience in your field" name="yearsExperience" value={form.yearsExperience}
                                       onChange={set} select options={['Less than 1 year', '1–3 years', '3–7 years', '7–15 years', 'Over 15 years']} />
                                <Field label="Have you received business financing before?" name="previousFinancing" value={form.previousFinancing}
                                       onChange={set} select options={['No, first time', 'Yes — bank loan', 'Yes — microfinance', 'Yes — grant / NGO', 'Yes — other']} />
                            </Grid>
                            <Grid cols={1}>
                                <Field label="Key achievements or proof of capability" name="keyAchievements" value={form.keyAchievements}
                                       onChange={set} multiline rows={3}
                                       placeholder="e.g. Produced 12 tonnes of catfish in 2023, supplied 3 hotels in Lagos..." required error={isError('keyAchievements')} />
                            </Grid>
                        </>
                    )}

                    {/* ── STEP 5 ── */}
                    {step === 5 && (
                        <>
                            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.purpleDark, mb: .75 }}>
                                Final Details
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: '.9rem', mb: 3.5, lineHeight: 1.7 }}>
                                Almost done. Tell us why you want to partner with NG Credit, and how you found us.
                            </Typography>
                            <SectionLabel>Your Motivation</SectionLabel>
                            <Grid cols={1}>
                                <Field label="Why do you want to partner with NG Credit?" name="whyNGCredit" value={form.whyNGCredit}
                                       onChange={set} multiline rows={4}
                                       placeholder="Tell us what excites you about this partnership and what you plan to build..." required error={isError('whyNGCredit')} />
                            </Grid>
                            <Grid cols={1}>
                                <Field label="How did you hear about us?" name="referralSource" value={form.referralSource}
                                       onChange={set} select
                                       options={['Social Media (Instagram / Twitter / LinkedIn)', 'Friend or Colleague Referral', 'News / Media Coverage', 'NG Credit Website', 'Event or Conference', 'Other']} />
                            </Grid>
                            <Box sx={{ mt: 3, p: 3, background: BRAND.lavenderSoft, borderRadius: 3, border: `1px solid ${alpha(BRAND.purple, .12)}` }}>
                                <Typography sx={{ fontSize: '.65rem', letterSpacing: '.14em', textTransform: 'uppercase',
                                    color: BRAND.purple, fontWeight: 700, mb: 1.5 }}>Application Summary</Typography>
                                {[
                                    ['Applicant', form.fullName], ['Email', form.email],
                                    ['Business', form.businessName], ['Type', form.partnerType],
                                    ['Asset Needed', form.assetType], ['Amount', form.amountNeeded],
                                ].map(([k, v]) => v && (
                                    <Box key={k} sx={{ display: 'flex', gap: 2, mb: .75 }}>
                                        <Typography sx={{ fontSize: '.78rem', color: 'text.secondary', width: 90, flexShrink: 0 }}>{k}</Typography>
                                        <Typography sx={{ fontSize: '.78rem', color: BRAND.purpleDark, fontWeight: 600 }}>{v}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box
                                    component="input" type="checkbox"
                                    checked={form.agreeTerms}
                                    onChange={e => set('agreeTerms', (e.target as HTMLInputElement).checked)}
                                    style={{ marginTop: 3, accentColor: BRAND.purple, cursor: 'pointer' }}
                                />
                                <Typography sx={{ fontSize: '.82rem', color: 'text.secondary', lineHeight: 1.65 }}>
                                    I confirm that the information provided is accurate and I consent to NG Credit
                                    contacting me at <strong>{form.email}</strong> regarding this application.
                                </Typography>
                            </Box>
                        </>
                    )}

                    {/* Navigation */}
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        mt: 4, pt: 3, borderTop: `1px solid ${alpha(BRAND.purple, .1)}`,
                        gap: 1,
                    }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack sx={{ fontSize: '.9rem !important', mr: -.5 }} />}
                            onClick={() => { setErrorFields([]); step > 1 ? setStep(s => s - 1) : navigate('/'); }}
                            size="small"
                            sx={{
                                borderColor: alpha(BRAND.purple, .3), color: BRAND.purple,
                                px: { xs: 1.25, sm: 2 }, py: .6,
                                fontSize: { xs: '.72rem', sm: '.8rem' },
                                whiteSpace: 'nowrap', minWidth: 0, flexShrink: 0,
                                '&:hover': { borderColor: BRAND.purple, background: alpha(BRAND.purple, .05) },
                            }}>
                            {step === 1 ? 'Home' : 'Back'}
                        </Button>

                        <Typography sx={{ fontSize: '.7rem', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            Step {step} of {STEPS.length}
                        </Typography>

                        {step < STEPS.length ? (
                            <Button
                                variant="contained"
                                endIcon={<ArrowForward sx={{ fontSize: '.9rem !important', ml: -.5 }} />}
                                onClick={handleNext}
                                size="small"
                                sx={{
                                    background: BRAND.purple,
                                    px: { xs: 1.25, sm: 2.5 }, py: .6,
                                    fontSize: { xs: '.72rem', sm: '.8rem' },
                                    whiteSpace: 'nowrap', minWidth: 0, flexShrink: 0,
                                    '&:hover': { background: BRAND.purpleLight, transform: 'translateY(-1px)',
                                        boxShadow: `0 8px 24px ${alpha(BRAND.purple, .4)}` },
                                    transition: 'all .25s',
                                }}>
                                Continue
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                endIcon={sending ? undefined : <ArrowForward sx={{ fontSize: '.9rem !important', ml: -.5 }} />}
                                disabled={!form.agreeTerms || sending}
                                onClick={handleSubmit}
                                size="small"
                                sx={{
                                    background: BRAND.purple,
                                    px: { xs: 1.25, sm: 2.5 }, py: .6,
                                    fontSize: { xs: '.72rem', sm: '.8rem' },
                                    whiteSpace: 'nowrap', minWidth: 0, flexShrink: 0,
                                    '&:hover': { background: BRAND.purpleLight },
                                    '&.Mui-disabled': { background: alpha(BRAND.purple, .35), color: '#fff' },
                                }}>
                                {sending ? 'Sending…' : 'Submit'}
                            </Button>
                        )}
                    </Box>
                </Box>

                <Typography sx={{ textAlign: 'center', mt: 3, fontSize: '.72rem', color: 'text.secondary' }}>
                    Applications are reviewed within 3–5 business days. Questions?{' '}
                    <Box component="a" href="mailto:corporate@ng-credit.com"
                         sx={{ color: BRAND.purple, textDecoration: 'none', fontWeight: 600 }}>
                        corporate@ng-credit.com
                    </Box>
                </Typography>
            </Container>

            <Snackbar open={snack} autoHideDuration={4000} onClose={() => setSnack(false)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setSnack(false)}>Application sent successfully!</Alert>
            </Snackbar>
        </ThemeProvider>
    );
};

export default Apply;